/*
 * Copyright (c) 2025, Wultra s.r.o. (www.wultra.com).
 *
 * All rights reserved. This source code can be used only for purposes specified
 * by the given license contract signed by the rightful deputy of Wultra s.r.o.
 * This source code can be used only by the owner of the license.
 *
 * Any disputes arising in respect of this agreement (license) shall be brought
 * before the Municipal Court of Prague.
 */

import { EventBus } from "./event-bus/event-bus"
import { ApkDetailService } from "./features/apk-detail/apk-detail-service"
import { ApkDetailStore } from "./features/apk-detail/apk-detail-store"
import { ApkDetailViewModel } from "./features/apk-detail/apk-detail-viewmodel"
import { AppState } from "./features/app/app-state"
import { ApplicationService } from "./features/application/application-service"
import { ApplicationStore } from "./features/application/application-store"
import { ApplicationViewModel } from "./features/application/application-viewmodel"
import { ApplicationDetailService } from "./features/application-detail/application-detail-service"
import { ApplicationDetailStore } from "./features/application-detail/application-detail-store"
import { ApplicationDetailViewModel } from "./features/application-detail/application-detail-viewmodel"
import { ApplicationSearchService } from "./features/application-search/application-search-service"
import { ApplicationSearchStore } from "./features/application-search/application-search-store"
import { ApplicationSearchViewmodel } from "./features/application-search/application-search-viewmodel"
import { ApplicationTypeService } from "./features/application-type/application-type-service"
import { ApplicationTypeStatsService } from "./features/application-type/application-type-stats-service"
import { ApplicationTypeStore } from "./features/application-type/application-type-store"
import { ApplicationTypeViewModel } from "./features/application-type/application-type-viewmodel"
import { OrganizationService } from "./features/organization/organization-service"
import { OrganizationStore } from "./features/organization/organization-store"
import { OrganizationViewModel } from "./features/organization/organization-viewmodel"
import { SessionService } from "./features/session/session-service"
import { SessionStore } from "./features/session/session-store"
import { SessionViewModel } from "./features/session/session-view-model"
import { UserAppTypeService } from "./features/user/user-apptype-service"
import { UserAppTypeStore } from "./features/user/user-apptype-store"
import { UserAppTypeViewModel } from "./features/user/user-apptype-viewmodel"
import { UserDetailService } from "./features/user/user-detail-service"
import { UserDetailStore } from "./features/user/user-detail-store"
import { UserDetailViewModel } from "./features/user/user-detail-view-model"
import { UserListService } from "./features/user/user-list-service"
import { UserListStore } from "./features/user/user-list-store"
import { UserListViewModel } from "./features/user/user-list-viewmodel"
import { UserRoleService } from "./features/user/user-role-service"
import { UserTotpService } from "./features/user/user-totp-service"
import { UserTotpStore } from "./features/user/user-totp-store"
import { UserTotpViewModel } from "./features/user/user-totp-viewmodel"
import { LoggerStore } from "./logger/logger-store"
import { LoggerViewModel } from "./logger/logger-viewmodel"
import { AppRoutes } from "./routes/routes"
import { forceLoginRedirect, forceRedirect404 } from "./utils/force-redirect"
import { isPublicRoute } from "./utils/is-public-route"
import { isValidRoute } from "./utils/is-valid-route"
import { ViewModelContainer } from "./viewmodel/viewmodel"

export interface AppComponents {
    vmContainer: ViewModelContainer
    eventBus: EventBus
    loggerStore: LoggerStore
    appState: AppState
}

/**
 * Sync init - called on app launch.
 * Creates lightweight instances of ViewModels and EventBus, all quick & synchronous code.
 * */
export function syncInit(): AppComponents {
    const eventBus = new EventBus()
    const loggerStore = new LoggerStore()
    const vmContainer = new ViewModelContainer()
    const appState = new AppState(eventBus)

    // register view model instances
    registerViewModelInstances(vmContainer, loggerStore, eventBus)

    // View models are created lazily; this one must exist before the first /me response
    // so it receives the CurrentUserLoaded event.
    vmContainer.getViewModel(ApplicationTypeViewModel)

    // attach handler for rejected promises
    handleRejectedPromises(loggerStore)

    return {
        vmContainer,
        eventBus,
        loggerStore,
        appState
    }
}

/**
 * Async init - called after the first React render.
 * Calls /me endpoint to detect if a user is logged in.
 * */
export async function asyncInit(appComponents: AppComponents): Promise<void> {
    try {
        // If there is invalid url, redirect to the page not found. Also stay on the page not found.
        if (!isValidRoute(location.pathname)) {
            if (location.pathname !== AppRoutes.PageNotFound) {
                forceRedirect404()
            }
            return
        }

        const sessionVM = appComponents.vmContainer.getViewModel(SessionViewModel)
        // do we have XSRF token in cookies? if not, we should fetch it first
        await sessionVM.fetchXSRFToken()

        // show login/reset password screen
        if (isPublicRoute(location.pathname)) return

        // continue to load user session

        // 1. maybe there is already stored current user
        if (sessionVM.currentUser) {
            // we have user, we are done here
            return
        }

        // 2. call /me endpoint; on success, user roles and app types are distributed via EventBus
        const user = await sessionVM.getCurrentUser(true)

        // if the call fails with 401 -> user will be automatically redirected from http-client
        if (!user.success) {
            // redirect to log in any way
            forceLoginRedirect()
        }
    } catch (err) {
        appComponents.loggerStore.logErrorMessage(
            `Initialization failed: ${err instanceof Error ? err.message : String(err)}`
        )
        forceLoginRedirect()
    }
}

function registerViewModelInstances(container: ViewModelContainer, loggerStore: LoggerStore, eventBus: EventBus) {
    // TODO: make sure to use WeakRef to break strong reference cycle in store-service-viewmodel
    // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/WeakRef
    container.registerViewModel(LoggerViewModel, () => {
        return new LoggerViewModel(loggerStore)
    })

    container.registerViewModel(OrganizationViewModel, () => {
        const store = new OrganizationStore()
        const service = new OrganizationService(store, loggerStore)
        return new OrganizationViewModel(store, service)
    })

    container.registerViewModel(UserDetailViewModel, (userId: number) => {
        const detailStore = new UserDetailStore()
        const userDetailService = new UserDetailService(detailStore, loggerStore)
        const userRoleService = new UserRoleService(detailStore, loggerStore)
        return new UserDetailViewModel(userId, detailStore, userDetailService, userRoleService)
    })

    container.registerViewModel(UserListViewModel, () => {
        const listStore = new UserListStore()
        const userListService = new UserListService(listStore, loggerStore)
        return new UserListViewModel(listStore, userListService)
    })

    container.registerViewModel(ApplicationTypeViewModel, () => {
        const store = new ApplicationTypeStore()
        const typeService = new ApplicationTypeService(store, loggerStore)
        const statsService = new ApplicationTypeStatsService(store, loggerStore)
        return new ApplicationTypeViewModel(store, typeService, statsService, eventBus, loggerStore)
    })

    container.registerViewModel(SessionViewModel, () => {
        const store = new SessionStore()
        const service = new SessionService(store, loggerStore)
        return new SessionViewModel(store, service, eventBus)
    })

    container.registerViewModel(ApplicationViewModel, () => {
        const store = new ApplicationStore()
        const service = new ApplicationService(store, loggerStore)
        return new ApplicationViewModel(store, service)
    })

    container.registerViewModel(UserAppTypeViewModel, () => {
        const store = new UserAppTypeStore()
        const service = new UserAppTypeService(store, loggerStore)
        return new UserAppTypeViewModel(store, service)
    })

    container.registerViewModel(ApplicationDetailViewModel, (appId: number) => {
        const store = new ApplicationDetailStore()
        const service = new ApplicationDetailService(store, loggerStore)
        return new ApplicationDetailViewModel(appId, store, service)
    })

    container.registerViewModel(ApkDetailViewModel, (apkId: number) => {
        const store = new ApkDetailStore()
        const service = new ApkDetailService(store, loggerStore)
        return new ApkDetailViewModel(apkId, store, service)
    })

    container.registerViewModel(ApplicationSearchViewmodel, () => {
        const store = new ApplicationSearchStore()
        const service = new ApplicationSearchService(loggerStore)
        const getDetailVM = (id: number) => container.getViewModel(ApplicationDetailViewModel, id)
        return new ApplicationSearchViewmodel(store, service, getDetailVM)
    })

    container.registerViewModel(UserTotpViewModel, () => {
        const store = new UserTotpStore()
        const service = new UserTotpService(store, loggerStore)
        return new UserTotpViewModel(store, service)
    })
}

// Handle for all rejected promises
function handleRejectedPromises(logger: LoggerStore) {
    window.addEventListener("unhandledrejection", (evt: PromiseRejectionEvent) => {
        logger.logErrorMessage(`Unhandled rejection: ${evt.reason}`)
        evt.preventDefault()
    })
}
